import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { post } from "../../lib/api";
import { useSession } from "../../lib/session";
import { canPushNow, pushSummary } from "../../lib/sessions";
import type { SessionDetail } from "../../lib/types";
import { Button } from "../ui/Button";
import { Notice } from "../ui/Feedback";
import { Pill } from "../ui/Status";
import { MicroLabel } from "../ui/Text";

/**
 * Who charged, and where — the session panel's side column.
 *
 * The facts an operator identifies a session by rather than judges it by, so they
 * sit beside the figures instead of above them. Each block leads with the name a
 * person would say out loud, set large, and carries the protocol identifiers
 * underneath it at data size for whoever has to match them against a log.
 */

const LINK =
  "text-micro font-semibold uppercase tracking-[0.11em] text-signal underline decoration-1 underline-offset-4 hover:no-underline";

export function SessionParty({ row }: { readonly row: SessionDetail }) {
  return (
    <aside className="flex min-w-0 flex-col gap-6 border-t border-rule pt-6 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-8">
      <section aria-label="Charged by" className="flex flex-col gap-1.5">
        <MicroLabel>Charged by</MicroLabel>
        <span
          className={`text-title font-semibold ${row.identityLabel === null ? "text-ink-faint" : "text-ink"}`}
        >
          {/* Spec 5 keeps the tag verbatim as evidence; an unnamed card has no
              other handle, so the tag is always shown beneath the name. */}
          {row.identityLabel ?? "Unknown card"}
        </span>
        <span className="font-mono text-data break-all text-ink-muted">{row.idTag}</span>
        {row.identityId === null ? null : (
          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
            <Link
              to={`/identities?identity=${encodeURIComponent(row.identityId)}`}
              className={LINK}
            >
              Card
            </Link>
            <Link
              to={`/sessions?identityId=${encodeURIComponent(row.identityId)}`}
              className={LINK}
            >
              Its sessions
            </Link>
          </div>
        )}
      </section>

      <section aria-label="Charged at" className="flex flex-col gap-1.5">
        <MicroLabel>Charged at</MicroLabel>
        <span className="text-section font-semibold text-ink">{row.chargePointId}</span>
        <span className="text-data text-ink-muted">
          EVSE {row.evseId} · connector {row.connectorId}
        </span>
        <span className="text-data text-ink-muted">{row.depotName}</span>
        <div className="pt-1">
          <Link to={`/fleet/${encodeURIComponent(row.chargePointId)}`} className={LINK}>
            Station
          </Link>
        </div>
      </section>

      <Pushes row={row} />
    </aside>
  );
}

/**
 * Where the session was pushed, and the Apps it never was: a session that closed
 * before an App's push was set is not sent by itself, only when asked here.
 */
function Pushes({ row }: { readonly row: SessionDetail }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const pushNow = useMutation({
    mutationFn: () => post<{ queued: number }>(`/sessions/${encodeURIComponent(row.id)}/push`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["session", row.id] }),
  });

  if (row.pushes.length === 0 && row.unsent.length === 0) return null;

  return (
    <section aria-label="Pushed to" className="flex flex-col gap-3">
      <MicroLabel>Pushed to</MicroLabel>
      {row.pushes.map((push) => {
        const summary = pushSummary(push);
        return (
          <PushLine key={push.app.id} app={push.app} tone={summary.tone} label={summary.label}>
            {summary.detail}
          </PushLine>
        );
      })}
      {row.unsent.map((app) => (
        <PushLine key={app.id} app={app} tone="idle" label="Never sent">
          Closed before this app's push was set
        </PushLine>
      ))}
      {row.pushes.some((push) => push.attempts > 0) && can("apps.view") ? (
        <Link
          to={`/session-pushes?sessionId=${encodeURIComponent(row.id)}`}
          className="text-data text-signal underline decoration-1 underline-offset-4 hover:no-underline"
        >
          Requests and answers
        </Link>
      ) : null}
      {canPushNow(row) && can("apps.update") ? (
        <div className="pt-1">
          <Button pending={pushNow.isPending} onClick={() => pushNow.mutate()}>
            Push now
          </Button>
        </div>
      ) : null}
      {pushNow.isSuccess ? (
        <Notice tone="ok">
          Queued for {pushNow.data.queued} app{pushNow.data.queued === 1 ? "" : "s"}.
        </Notice>
      ) : null}
      {pushNow.isError ? <Notice tone="error">{pushNow.error.message}</Notice> : null}
    </section>
  );
}

function PushLine({
  app,
  tone,
  label,
  children,
}: {
  readonly app: { readonly id: string; readonly name: string };
  readonly tone: ReturnType<typeof pushSummary>["tone"];
  readonly label: string;
  readonly children: string | null;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to={`/apps/${encodeURIComponent(app.id)}`}
          className="text-data font-semibold text-ink hover:underline"
        >
          {app.name}
        </Link>
        <Pill tone={tone}>{label}</Pill>
      </div>
      {children === null ? null : (
        <span className="font-mono text-data break-all text-ink-muted">{children}</span>
      )}
    </div>
  );
}
